import type { BusinessAddressRef } from '@seek/hirer-business-address';
import { Checkout } from '@seek/hirer-ecommerce';
import { useOrderSummaryDetailsLazyQuery } from '@seek/hirer-ecommerce/graphql-types';
import {
  PaymentCardUnsupportedError,
  PaymentConfirmationError,
  PaymentGeneralError,
  PaymentSavedCreditCardPayError,
  type PaymentMethodType,
} from '@seek/payments-widget';
import { useTranslations } from '@vocab/react';
import { Drawer, Text } from 'braid-design-system';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { SECTION_BUSINESS_ADDRESS } from 'src/app/shared/constants/pageSectionName';
import { POSTED_JOB_ID } from 'src/app/shared/constants/queryParams';
import {
  ROUTE_SELECT_AD_TYPE,
  ROUTE_SUCCESS,
} from 'src/app/shared/constants/routes';
import { EVENT_NAME_CONTRACT_PURCHASE_VIEWED } from 'src/app/shared/dataAnalytics/schema/constants';
import { saveTrackingData } from 'src/app/shared/dataAnalytics/storage';
import { useConfig } from 'src/app/shared/hooks/useConfig';
import { useScroller } from 'src/app/shared/hooks/useScroller';
import { useShoppingCart } from 'src/app/shared/hooks/useShoppingCart';
import { getEnvironment } from 'src/app/utils/environment';
import { log } from 'src/app/utils/logging';
import { BackButton } from 'src/app/v2/components/BackButton';
import { PaymentInProgressDialog } from 'src/app/v2/components/PaymentInProgressDialog';
import { PreviewButton } from 'src/app/v2/components/PreviewButton';
import {
  BusinessAddressValidationError,
  DraftClosedError,
} from 'src/app/v2/constants/errors';
import { useHirerInfoContext } from 'src/app/v2/contexts/hirerInfo';
import { useAnalyticsTracking } from 'src/app/v2/hooks/useAnalyticsTracking';
import { useBrandLazyQuery } from 'src/app/v2/hooks/useBrandLazyQuery';
import { useCategoryLazyQuery } from 'src/app/v2/hooks/useCategoryLazyQuery';
import { useCloseDraft } from 'src/app/v2/hooks/useCloseDraft';
import { useDraft } from 'src/app/v2/hooks/useDraft';
import { useGetDraftLazyQuery } from 'src/app/v2/hooks/useGetDraftLazyQuery';
import { useLocationQuery } from 'src/app/v2/hooks/useLocationQuery';
import { useQueryParam } from 'src/app/v2/hooks/useQueryParam';
import { useQuestionsLazyQuery } from 'src/app/v2/hooks/useQuestionsLazyQuery';
import { useLocaliseRoute } from 'src/app/v2/utils/useLocaliseRoute';
import { DraftJobFlowType } from 'src/graphql/generated';
import type { CheckoutComponentCallbacks } from 'src/types/checkout';
import { TealiumContractType, TealiumPricingType } from 'src/types/tealium';

import translations from './.vocab';
import {
  onPostAdButtonClicked,
  onReplaceSavedCard,
  onSaveCardOptionChanged,
  onSavedCardLoaded,
  onPaymentOptionChanged,
} from './callbacks';

interface Props {
  jobId?: string;
  jobIdempotencyKey: string;
  children: React.ReactNode;
  businessAddressRef?: React.RefObject<BusinessAddressRef>;
  onCheckoutFailed: (error: Error) => void;
  onBackButtonClicked: () => void;
  setCheckoutError: (error: Error | null) => void;
}

export const CheckoutSection = ({
  jobId,
  jobIdempotencyKey,
  businessAddressRef,
  children,
  onCheckoutFailed,
  onBackButtonClicked,
  setCheckoutError,
}: Props) => {
  const [selectedPaymentOption, setSelectedPaymentOption] =
    useState<PaymentMethodType>('CreditCard');

  const paymentsReturnValue = useQueryParam('payments_return');
  const isReturnedFrom3rdPayment = Boolean(paymentsReturnValue);
  const [isPaymentInProgressDialogOpen, setIsPaymentInProgressDialogOpen] =
    useState(isReturnedFrom3rdPayment);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const draft = useDraft();
  const { seekAdvertiserId, userId } = useHirerInfoContext();
  const [getDraft] = useGetDraftLazyQuery(draft!.id);
  const [closeDraft] = useCloseDraft();
  const { getSite } = useConfig();
  const { t } = useTranslations(translations);
  const { trackingAdapter } = useAnalyticsTracking();
  const navigate = useNavigate();
  const scrollTo = useScroller();
  const localiseRoute = useLocaliseRoute();
  const {
    cartState: { items } = {},
    orderState: { order: { orderId } = {} },
  } = useShoppingCart();

  const cartItemWithJobId = items?.find((item) =>
    // @ts-ignore
    Boolean(item.product?.job?.id),
  );
  // @ts-ignore
  const jobIdFromCart = cartItemWithJobId?.product?.job?.id;

  const [getOrderSummaryDetailsTotal] = useOrderSummaryDetailsLazyQuery();
  const [getBrandQuery] = useBrandLazyQuery(draft?.branding?.id || '');
  const location = useLocationQuery(draft?.locations?.[0]?.seekId || '');

  const [getSuggestedCategoriesQuery] = useCategoryLazyQuery(
    draft?.titles?.[0]?.title || '',
    location?.country || '',
  );

  const [getQuestionsQuery] = useQuestionsLazyQuery(
    draft?.questionnaireId || '',
  );

  const basicLog = {
    draftId: draft?.id,
    jobIdFromCart,
  };

  const updateAdBudget = async () => {
    const hasAdBudgetInCart = items?.find(
      (item) => item.product.__typename === 'AdBudgetCartItem',
    );

    if (hasAdBudgetInCart && orderId) {
      const orderSummary = await getOrderSummaryDetailsTotal({
        variables: {
          orderId,
          advertiserNation: 'test-advertiser-nation',
        },
        fetchPolicy: 'cache-only',
      });
      const orderLineItems = orderSummary.data?.orderSummary?.lineItems || [];
      const adbudgetPurchased = orderLineItems?.find(
        (item) => item?.product?.productType === 'AD_BUDGET',
      );
      const allowance = adbudgetPurchased?.price.netPrice.value ?? 0;

      const productPrice =
        orderSummary.data?.orderSummary?.lineItems
          .filter((item) => item?.product?.productType !== 'AD_BUDGET')
          .reduce((acc, item) => acc + item?.price?.netPrice?.value, 0) ?? 0;

      saveTrackingData({
        adBudgetPurchased: EVENT_NAME_CONTRACT_PURCHASE_VIEWED,
        hirerContract: {
          adType: adbudgetPurchased?.product.name,
          value: allowance,
          balance: Number(allowance) - Number(productPrice),
          type: 'seek-contract',
        },
        advertiser: {
          type: TealiumContractType.Contract,
          pricingType: TealiumPricingType.Variable,
        },
      });
    }
  };

  const onPaymentSuccess: CheckoutComponentCallbacks['onPaymentSuccess'] =
    async () => {
      try {
        await updateAdBudget();
        await closeDraft({
          flowType: DraftJobFlowType.CREATE,
        });
      } catch (error) {
        log.error('CheckoutSection | onPaymentSuccess | error', {
          error,
          ...basicLog,
        });
      }

      navigate({
        pathname: localiseRoute({ path: ROUTE_SUCCESS }),
        // NOTE: do NOT use preserved name "jobid" as many logics depend on this naming
        // Check getJobIdParam for usages
        search: `?${POSTED_JOB_ID}=${jobIdFromCart}`,
      });
    };

  const onTrackingEvent: CheckoutComponentCallbacks['onTrackingEvent'] = (
    payload,
  ) => {
    if (payload.tracking) {
      trackingAdapter.sendLinkEvent(payload.tracking);
    }
  };

  const prefetchPreviewData = () => {
    Promise.all([
      draft?.branding?.id ? getBrandQuery() : Promise.resolve(),
      draft?.categories && location?.country
        ? getSuggestedCategoriesQuery()
        : Promise.resolve(),
      draft?.questionnaireId ? getQuestionsQuery() : Promise.resolve(),
    ]).catch((error) => {
      log.error('CheckoutSection | Error prefetching preview data', { error });
    });
  };

  const onPostAdButtonClick: CheckoutComponentCallbacks['onPostAdButtonClick'] =
    async (args) => {
      setIsPaymentInProgressDialogOpen(true);
      setCheckoutError(null);
      if (jobId !== jobIdFromCart) {
        log.error('CheckoutSection | onPostAdButtonClick | jobId mismatch', {
          seekAdvertiserId,
          userId,
          jobId,
          orderId,
          isReturnedFrom3rdPayment,
          cartItem: JSON.stringify(items),
          ...basicLog,
        });
      }

      await onPostAdButtonClicked({
        businessAddressRef,
        getDraft,
        ...args,
      });
    };

  const onPostAdButtonClickFailed: CheckoutComponentCallbacks['onPostAdButtonClickFailed'] =
    (error) => {
      setIsPaymentInProgressDialogOpen(false);

      if (
        error instanceof BusinessAddressValidationError &&
        error.message === 'Business address validation failed'
      ) {
        scrollTo(SECTION_BUSINESS_ADDRESS);
        return;
      }

      if (
        error instanceof DraftClosedError &&
        error.message === 'Draft is no longer open'
      ) {
        log.error(
          'CheckoutSection | onPostAdButtonClickFailed | Multi tab posting detected draft is already closed',
          {
            error,
            ...basicLog,
          },
        );
        // DraftLoader will detect a change in draft status and redirect to error page
        return;
      }

      log.error(
        'CheckoutSection | onPostAdButtonClickFailed | error posting ad',
        {
          error,
          ...basicLog,
        },
      );
    };

  const onPaymentFailed: CheckoutComponentCallbacks['onPaymentFailed'] = (
    error,
  ) => {
    setIsPaymentInProgressDialogOpen(false);

    const expectedError = [
      PaymentCardUnsupportedError,
      PaymentConfirmationError,
      PaymentGeneralError,
      PaymentSavedCreditCardPayError,
    ].find((err) => error instanceof err);

    if (expectedError) {
      return;
    }

    log.error(
      'CheckoutSection | onPaymentFailed | Payment failed with unexpected error',
      { error, ...basicLog },
    );

    onCheckoutFailed(error);
  };

  const notifyFatalError: CheckoutComponentCallbacks['notifyFatalError'] = (
    error,
  ) => {
    setIsPaymentInProgressDialogOpen(false);

    if (error.reason === 'product_unavailable') {
      log.error('CheckoutSection | notifyFatalError | product_unavailable', {
        error,
        ...basicLog,
      });

      navigate(localiseRoute({ path: ROUTE_SELECT_AD_TYPE }));

      return;
    }

    // NOTE: Checkout component itself renders an alert upon create_order_fatal_error
    if (error.reason === 'create_order_fatal_error') {
      log.error(
        'CheckoutSection | notifyFatalError | create_order_fatal_error',
        { error, ...basicLog },
      );

      return;
    }

    onCheckoutFailed(new Error(error.reason));
  };

  const handleCancelledPayment: CheckoutComponentCallbacks['handleCancelledPayment'] =
    () => {
      setIsPaymentInProgressDialogOpen(false);
    };

  return (
    <>
      <PaymentInProgressDialog
        isOpen={isPaymentInProgressDialogOpen}
        setIsOpen={setIsPaymentInProgressDialogOpen}
      />
      <Checkout
        env={getEnvironment()}
        callbacks={{
          handleCancelledPayment,
          handlePaymentOptionChange: onPaymentOptionChanged(
            setSelectedPaymentOption,
          ),
          onPostAdButtonClick,
          notifyFatalError,
          onPaymentFailed,
          onPaymentSuccess,
          onPostAdButtonClickFailed,
          onReplaceSavedCard,
          onSaveCardOptionChanged,
          onSavedCardLoaded,
          onTrackingEvent,
        }}
        paymentMethodSelected={selectedPaymentOption}
        idempotencyKey={jobIdempotencyKey}
        site={getSite()}
        useCase="CAJA"
        backButtonComponent={
          <BackButton onBackButtonClicked={onBackButtonClicked} />
        }
        previewButtonComponent={
          <PreviewButton
            onClicked={() => setIsPreviewOpen((prevState) => !prevState)}
            onMouseOver={prefetchPreviewData}
          />
        }
      />
      <Drawer
        id="preview"
        title={t('preview-title')}
        description={<Text tone="secondary">{t('preview-description')}</Text>}
        open={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        width="large"
      >
        {children}
      </Drawer>
    </>
  );
};
